import { Box, Button, Chip, CircularProgress, Paper, Stack, Typography, useTheme } from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import ApiClient from '../../backend/ApiClient'
import algo from '../../core/Algo'
import { IPaginationQueryResponse } from '../../definitions/Common'
import { IProviderExpense } from '../../definitions/ServiceExpense'
import { useAppSelector } from '../../store/Hooks'
import { HorizontalBarChart } from '../charts'
import LabeledChart from '../controls/data_visualization/LabeledChart'
import useInfiniteScroll from '../hooks/useInfinityScroll'
import { IRouteParameters } from '../routes/ContractDetails'
import RoutingManager from '../routing/Routing'

interface P {
    contractId: number
    title: string
    subtitle?: string
    startDate: string
    endDate: string
    triggerSnackbarError?: (view: string) => void
    updateDataFlag: boolean
}

const limit = 30

function ExpenseByTosSection({ contractId, title, subtitle, startDate, endDate, triggerSnackbarError, updateDataFlag }: P) {
    const theme = useTheme()
    const { number } = algo
    const params = RoutingManager.getParams<IRouteParameters>(useAppSelector(s => s.session.routeParams))

    const [items, setItems] = useState<IProviderExpense[]>([])
    const [itemsOffset, setItemsOffset] = useState(0)
    const [isLoading, setIsLoading] = useState(true)
    const [hasError, setHasError] = useState(false)
    const itemsContainerRef = useRef(null)
    const [hasMore, setHasMore] = useState(true)

    const loadItems = async (offset: number, view: string) => {
        try {
            setIsLoading(true)
            let result: IPaginationQueryResponse<IProviderExpense>
            switch (view) {
                case 'pmpm':
                    result = await ApiClient.getContractExpensesPMPMGroupedByProvider({ contractId, startDate, endDate, limit, offset: offset ?? 0 })
                    break
                case 'default':
                    result = await ApiClient.getContractExpensesGroupedByProvider({
                        contractId,
                        startDate,
                        endDate,
                        limit,
                        offset: offset ?? 0
                    })
                    break
            }

            if (offset === 0) {
                setItems(result.items)
            } else {
                setItems(prev => [...prev, ...result.items])
            }
            setItemsOffset(prev => prev + limit)
            setHasMore(result.hasNextPage)
            return result
        } catch (error) {
            setHasError(true)
            triggerSnackbarError('There was an error during data fetch: ' + error?.message)
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        const performUpdate = async () => {
            setItems([])
            setItemsOffset(0)
            await loadItems(0, params?.dataViewMode)
        }
        performUpdate()
    }, [updateDataFlag, params?.dataViewMode])

    const { lastElementRef } = useInfiniteScroll({ hasMore: hasMore, isLoading: isLoading, onLoadMore: () => loadItems(itemsOffset, params?.dataViewMode), threshold: 1, root: itemsContainerRef.current })

    return (
        <Paper sx={{ height: '100%', pt: 3, pl: 3, pb: 3 }}>
            <Stack direction='row' alignItems='center' gap={1} sx={{ marginBottom: 2 }}>
                { params?.dataViewMode && (
                    <Chip size='small' variant='outlined' label={params?.dataViewMode === 'pmpm' ? 'PMPM' : 'Total'} />
                )}
                <Stack>
                    <Typography variant='h6'>{title}</Typography>
                    {subtitle && (
                        <Typography variant='body2'>{subtitle}</Typography>
                    )}
                </Stack>
            </Stack>

            <Stack sx={{ height: 'calc(100% - 42px)', overflowY: 'auto' }} ref={itemsContainerRef}>
                {items?.length > 0
                    ? (
                            <>
                                {items.map((providerExpenses, i) => {
                                    return (
                                        <Box key={i} ref={i === (items.length - 1) ? lastElementRef : null}>
                                            <LabeledChart
                                                label={providerExpenses.providerName}>
                                                <HorizontalBarChart
                                                    isLoading={false}
                                                    data={
                                                        {
                                                            labels: providerExpenses.expenses.map(e => e.name),
                                                            datasets: [{
                                                                data: providerExpenses.expenses.map(e => e.value),
                                                                backgroundColor: [theme.palette.info.main, theme.palette.primary.light, theme.palette.primary.main, theme.palette.secondary.main, theme.palette.primary.dark]
                                                            }]
                                                        }
                                                    }
                                                    options={{
                                                        plugins: {
                                                            datalabels: {
                                                                color: context => {
                                                                    const yAxisData = context.dataset.data as number[]
                                                                    const currentValue = yAxisData[context.dataIndex]
                                                                    const max = Math.max(...yAxisData)
                                                                    return (currentValue / max) < 0.5 ? theme.palette.text.secondary : theme.palette.secondary.contrastText
                                                                },
                                                                anchor: 'end',
                                                                align: context => {
                                                                    const yAxisData = context.dataset.data as number[]
                                                                    const currentValue = yAxisData[context.dataIndex]
                                                                    const max = Math.max(...yAxisData)
                                                                    return (currentValue / max) < 0.5 ? 'end' : 'start'
                                                                },
                                                                offset: 10,
                                                                formatter: value => '$ ' + number(value)
                                                            },
                                                            legend: {
                                                                display: false
                                                            }
                                                        },
                                                        scales: {
                                                            x: {
                                                                display: false,
                                                                grid: {
                                                                    display: false
                                                                }
                                                            },
                                                            y: {
                                                                grid: {
                                                                    display: false
                                                                }
                                                            }
                                                        }
                                                    }}
                                                />
                                            </LabeledChart>
                                        </Box>
                                    )
                                })}
                                {
                                    (isLoading && hasMore) && (
                                        <Stack justifyContent='center' alignItems='center' p={3} mb={3}>
                                            <CircularProgress />
                                        </Stack>
                                    )
                                }
                                {
                                    hasError && (
                                        <Stack justifyContent='center' alignItems='center' p={3} mb={3}>
                                            <Button
                                                variant='contained'
                                                onClick={() => {
                                                    loadItems(0, params?.dataViewMode)
                                                    setHasError(false)
                                                }}>
                                                Retry loading
                                            </Button>
                                        </Stack>
                                    )
                                }
                            </>
                        )
                    : isLoading
                        ? (
                                <Stack sx={{ maxHeight: 300, mt: 4 }} flex={1}>
                                    <HorizontalBarChart
                                        data={
                                            {
                                                labels: [],
                                                datasets: []
                                            }
                                        }
                                        isLoading={true}
                                    />
                                </Stack>
                            )
                        : (
                                <Box height='100%' display='flex' justifyContent='center' alignItems='center'>
                                    <Typography>
                                        No data available for the selected time period
                                    </Typography>
                                </Box>
                            )}
            </Stack>
        </Paper>
    )
}

export default ExpenseByTosSection
